import React from 'react';
import { TemplateProps } from '../../../types/cv';
import { Mail, Phone, MapPin } from 'lucide-react';
import { CV_LABELS, frPresent } from '../../../utils/cvLabels';
import { isSectionContinuation } from '../../../utils/cvPagination';
import SectionHeading from '../SectionHeading';

/** Elegance ivoirienne — vert foret & or, photo centree, colonnes claires. */
const IvoirienElegantTemplate: React.FC<TemplateProps> = ({
    data,
    isContinuationPage = false,
    showPlaceholders = false,
    pageIndex = 0,
    fullData,
}) => {
    const primaryColor = data.style?.primaryColor || '#166534';
    const accent = '#c9a227';
    const {
        fontFamily = 'font-serif',
        textColor = '#1c1917',
        backgroundColor = '#fffef9',
        sectionBackgroundColor = primaryColor,
    } = data.style || {};

    const source = fullData || data;
    const eduContinued = isSectionContinuation(source, 'education', pageIndex);
    const expContinued = isSectionContinuation(source, 'experience', pageIndex);
    const skillsContinued = isSectionContinuation(source, 'skills', pageIndex);
    const langContinued = isSectionContinuation(source, 'languages', pageIndex);

    const phone = data.personalInfo.phone || (showPlaceholders ? '+225 07 00 00 00 00' : '');
    const email = data.personalInfo.email || (showPlaceholders ? 'contact@email.ci' : '');
    const address =
        data.personalInfo.address ||
        (showPlaceholders ? 'Cocody, Abidjan' : '');
    const summary =
        data.personalInfo.summary ||
        (showPlaceholders
            ? 'Professionnel motive, serieux et oriente resultats, avec une solide experience sur le marche ivoirien.'
            : '');
    const photoSrc = data.personalInfo.profileImage || '';

    const skills: any[] =
        data.skills?.length
            ? data.skills
            : showPlaceholders
              ? [{ name: 'Gestion', level: 80 }, { name: 'Excel', level: 70 }]
              : [];
    const languages: any[] =
        data.languages?.length
            ? data.languages
            : showPlaceholders
              ? [{ name: 'Francais', level: 100 }, { name: 'Anglais', level: 70 }]
              : [];
    const experience: any[] =
        data.experience?.length
            ? data.experience
            : showPlaceholders
              ? [
                    {
                        position: 'Assistante',
                        company: 'Entreprise SA',
                        startDate: '2021',
                        endDate: '2024',
                        description: 'Gestion du secretariat et relation clients.',
                        isCurrent: false,
                    },
                ]
              : [];
    const education: any[] =
        data.education?.length
            ? data.education
            : showPlaceholders
              ? [
                    {
                        degree: 'Licence',
                        field: 'Gestion',
                        institution: 'UFHB',
                        startDate: '2017',
                        endDate: '2020',
                        isCurrent: false,
                    },
                ]
              : [];

    return (
        <div
            className={`shadow-2xl cv-page-sheet h-[297mm] max-h-[297mm] w-full flex overflow-hidden ${fontFamily}`}
            style={{ backgroundColor, color: textColor }}
        >
            <div
                data-cv-sidebar
                className="w-[34%] text-white p-8 flex flex-col overflow-hidden"
                style={{ backgroundColor: sectionBackgroundColor }}
            >
                {isContinuationPage ? (
                    <div className="mt-2">
                        <p className="cv-fs-name text-xs font-bold uppercase tracking-widest text-white/80">
                            {data.personalInfo.firstName} {data.personalInfo.lastName}
                        </p>
                        <p className="text-[10px] uppercase tracking-wider text-white/40 mt-2">
                            {CV_LABELS.suite}
                        </p>
                    </div>
                ) : (
                    <>
                        {(photoSrc || showPlaceholders) && (
                            <div className="mb-6 flex justify-center">
                                <div
                                    className="w-36 h-36 rounded-full overflow-hidden bg-white/20 border-4"
                                    style={{ borderColor: accent }}
                                >
                                    {photoSrc ? (
                                        <img src={photoSrc} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full bg-emerald-800" />
                                    )}
                                </div>
                            </div>
                        )}
                        <div className="cv-fs-contact space-y-3 mb-8 text-sm">
                            {phone && (
                                <div className="flex items-start gap-2">
                                    <Phone className="w-4 h-4 mt-0.5 shrink-0" style={{ color: accent }} />
                                    <span>{phone}</span>
                                </div>
                            )}
                            {email && (
                                <div className="flex items-start gap-2">
                                    <Mail className="w-4 h-4 mt-0.5 shrink-0" style={{ color: accent }} />
                                    <span className="break-all">{email}</span>
                                </div>
                            )}
                            {address && (
                                <div className="flex items-start gap-2">
                                    <MapPin className="w-4 h-4 mt-0.5 shrink-0" style={{ color: accent }} />
                                    <span>{address}</span>
                                </div>
                            )}
                        </div>
                    </>
                )}

                <section className="mb-6">
                    <SectionHeading
                        title={CV_LABELS.skills}
                        isContinuation={skillsContinued}
                        primaryColor="#ffffff"
                        variant="bar"
                    />
                    <div className="cv-fs-skills space-y-2 mt-3">
                        {skills.map((s, i) => (
                            <div
                                key={s.id || i}
                                data-cv-block
                                data-cv-item-id={s.id || undefined}
                                className="text-sm"
                            >
                                <div className="flex justify-between mb-1">
                                    <span>{s.name}</span>
                                </div>
                                <div className="h-1.5 bg-white/20 rounded-full overflow-hidden">
                                    <div
                                        className="h-full rounded-full"
                                        style={{
                                            width: `${typeof s.level === 'number' ? s.level : 70}%`,
                                            backgroundColor: accent,
                                        }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                <section className="mb-6">
                    <SectionHeading
                        title={CV_LABELS.languages}
                        isContinuation={langContinued}
                        primaryColor="#ffffff"
                        variant="bar"
                    />
                    <div className="cv-fs-languages space-y-2 mt-3">
                        {languages.map((l, i) => (
                            <div
                                key={l.id || i}
                                data-cv-block
                                data-cv-item-id={l.id || undefined}
                                className="text-sm flex justify-between"
                            >
                                <span>{l.name}</span>
                                <span className="text-white/70 text-xs">
                                    {typeof l.level === 'number' ? `${l.level}%` : l.level || ''}
                                </span>
                            </div>
                        ))}
                    </div>
                </section>
            </div>

            <div data-cv-main className="flex-1 p-10 overflow-hidden">
                {!isContinuationPage && (
                    <header className="mb-6 pb-4 border-b-2" style={{ borderColor: accent }}>
                        <h1 className="cv-fs-name text-3xl font-bold tracking-tight" style={{ color: primaryColor }}>
                            {data.personalInfo.firstName || (showPlaceholders ? 'Aya' : '')}{' '}
                            {data.personalInfo.lastName || (showPlaceholders ? 'Traore' : '')}
                        </h1>
                        <p className="text-sm uppercase tracking-[0.2em] mt-1" style={{ color: accent }}>
                            {data.personalInfo.profession ||
                                (showPlaceholders ? 'Assistante administrative' : '')}
                        </p>
                        {summary && <p className="cv-fs-summary mt-4 text-sm leading-relaxed">{summary}</p>}
                    </header>
                )}

                <section className="mb-6">
                    <SectionHeading
                        title={CV_LABELS.experience}
                        isContinuation={expContinued}
                        primaryColor={primaryColor}
                        variant="bar"
                    />
                    <div className="cv-fs-experience space-y-4 mt-3">
                        {experience.map((exp, i) => (
                            <div key={exp.id || i} data-cv-block data-cv-item-id={exp.id || undefined}>
                                <div className="flex justify-between gap-2">
                                    <p className="font-bold text-sm">{exp.position}</p>
                                    <p className="text-xs text-stone-500 whitespace-nowrap">
                                        {exp.startDate} — {frPresent(!!exp.isCurrent, exp.endDate || '')}
                                    </p>
                                </div>
                                <p className="text-xs font-medium" style={{ color: primaryColor }}>
                                    {exp.company}
                                </p>
                                {exp.description && (
                                    <p className="text-xs mt-1 text-stone-600 leading-relaxed">
                                        {exp.description}
                                    </p>
                                )}
                            </div>
                        ))}
                    </div>
                </section>

                <section className="mb-6">
                    <SectionHeading
                        title={CV_LABELS.education}
                        isContinuation={eduContinued}
                        primaryColor={primaryColor}
                        variant="bar"
                    />
                    <div className="cv-fs-education space-y-3 mt-3">
                        {education.map((edu, i) => (
                            <div key={edu.id || i} data-cv-block data-cv-item-id={edu.id || undefined}>
                                <p className="font-bold text-sm">
                                    {edu.degree}
                                    {edu.field ? ` — ${edu.field}` : ''}
                                </p>
                                <p className="text-xs" style={{ color: primaryColor }}>
                                    {edu.institution}
                                </p>
                                <p className="text-xs text-stone-500">
                                    {edu.startDate} — {frPresent(!!edu.isCurrent, edu.endDate || '')}
                                </p>
                                {edu.description && (
                                    <p className="text-xs mt-1 text-stone-600 leading-relaxed">
                                        {edu.description}
                                    </p>
                                )}
                            </div>
                        ))}
                    </div>
                </section>
            </div>
        </div>
    );
};

export default IvoirienElegantTemplate;
